import type { FeatureType, PackedLayer, SliceEstimate } from '../slice/types';
import { FEATURE_TYPES } from '../slice/types';

export type PreviewStatus = 'idle' | 'slicing' | 'ready' | 'error';
export type ColorBy = 'feature' | 'filament';

export interface PreviewState {
  status: PreviewStatus;
  progress: number;
  message: string;
  layers: PackedLayer[];
  currentLayer: number;
  layerDraw: number;
  colorBy: ColorBy;
  hidden: Partial<Record<FeatureType, boolean>>;
  playing: boolean;
  estimate: SliceEstimate | null;
  elapsedMs: number;
  triangleCount: number;
  segmentCount: number;
  hash: string;
  error: string | null;
  requestId: number;
}

type Listener = () => void;

const hidden0 = (): PreviewState['hidden'] => {
  const h: PreviewState['hidden'] = {};
  for (const k of FEATURE_TYPES) h[k] = false;
  return h;
};

let state: PreviewState = {
  status: 'idle',
  progress: 0,
  message: '',
  layers: [],
  currentLayer: 0,
  layerDraw: 1,
  colorBy: 'feature',
  hidden: hidden0(),
  playing: false,
  estimate: null,
  elapsedMs: 0,
  triangleCount: 0,
  segmentCount: 0,
  hash: '',
  error: null,
  requestId: 0,
};

const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l();
}

export const previewStore = {
  getSnapshot(): PreviewState {
    return state;
  },
  subscribe(fn: Listener) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  set(patch: Partial<PreviewState>) {
    state = { ...state, ...patch };
    emit();
  },
  resetForSlice(hash: string, requestId: number) {
    state = {
      ...state,
      status: 'slicing',
      progress: 0,
      message: 'Slicing… 0%',
      layers: [],
      currentLayer: 0,
      layerDraw: 1,
      estimate: null,
      elapsedMs: 0,
      triangleCount: 0,
      segmentCount: 0,
      hash,
      error: null,
      requestId,
      playing: false,
    };
    emit();
  },
  appendLayer(layer: PackedLayer, pct: number, message: string) {
    if (layer.index !== state.layers.length) {
      const next = state.layers.slice();
      next[layer.index] = layer;
      state = { ...state, layers: next, progress: pct, message, status: 'slicing' };
    } else {
      state = {
        ...state,
        layers: [...state.layers, layer],
        progress: pct,
        message,
        status: 'slicing',
        currentLayer: state.layers.length === 0 ? 0 : state.currentLayer,
      };
    }
    emit();
  },
  toggleFeature(type: FeatureType) {
    state = { ...state, hidden: { ...state.hidden, [type]: !state.hidden[type] } };
    emit();
  },
  step(delta: number) {
    const max = Math.max(0, state.layers.length - 1);
    const next = Math.min(max, Math.max(0, state.currentLayer + delta));
    if (next === state.currentLayer) return;
    state = { ...state, currentLayer: next, layerDraw: 1 };
    emit();
  },
  setLayer(index: number) {
    const max = Math.max(0, state.layers.length - 1);
    const next = Math.min(max, Math.max(0, Math.round(index)));
    state = { ...state, currentLayer: next, layerDraw: 1, playing: false };
    emit();
  },
};

export function hashSliceInput(
  objects: {
    id: string;
    positions: Float32Array;
    position: number[];
    rotation: number[];
    scale: number[];
    overrides: Record<string, string | number | boolean>;
    paintSupports?: Uint8Array;
  }[],
  settings: Record<string, string | number | boolean>
): string {
  const keys = [
    'layer_height', 'initial_layer_print_height', 'wall_loops',
    'outer_wall_line_width', 'inner_wall_line_width', 'sparse_infill_line_width',
    'sparse_infill_density', 'sparse_infill_pattern', 'top_shell_layers',
    'bottom_shell_layers', 'enable_support', 'support_threshold_angle',
    'brim_type', 'brim_width', 'brim_object_gap', 'skirt_loops', 'skirt_distance',
    'skirt_height', 'filament_colour', 'filament_density', 'filament_diameter',
    'outer_wall_speed', 'inner_wall_speed', 'sparse_infill_speed',
    'internal_solid_infill_speed', 'support_speed', 'initial_layer_speed',
  ];
  let h = 2166136261;
  const mix = (n: number) => {
    h ^= n | 0;
    h = Math.imul(h, 16777619);
  };
  const str = (s: string) => {
    for (let i = 0; i < s.length; i++) mix(s.charCodeAt(i));
  };
  for (const k of keys) str(`${k}:${String(settings[k] ?? '')}`);
  mix(objects.length);
  for (const o of objects) {
    str(o.id);
    mix(o.positions.length);
    for (let i = 0; i < o.positions.length; i += 17) mix(Math.round(o.positions[i] * 100));
    for (const n of [...o.position, ...o.rotation, ...o.scale]) mix(Math.round(n * 1000));
    str(JSON.stringify(o.overrides || {}));
    if (o.paintSupports) {
      let s = 0;
      for (let i = 0; i < o.paintSupports.length; i++) s = (s + o.paintSupports[i] * (i + 1)) | 0;
      mix(s);
    }
  }
  return (h >>> 0).toString(16);
}
